/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Text,
  Button,
  Input,
  FormControl,
  FormLabel,
  Card,
  CardBody,
  Flex,
  Switch,
  useToast,
  useQuery,
  VStack,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { AddIcon } from '@chakra-ui/icons';
import { getSystemAxisListById } from 'api/auth.api';
import { useMutation } from '@tanstack/react-query';
import { createSystemAxis } from 'api/auth.api';
import { updateSystemAxis } from 'api/auth.api';
import BottleItem from 'components/BottleEditor';
import { getSavedDiseaseProtocolById } from 'api/auth.api';

function SavedDiseaseProtocolForm({ editId, onSuccess }) {
  const id = editId?._id;
  const [dataEdit, setDataEdit] = useState({});
  const toast = useToast();
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      diseaseEnglish: '',
      diseaseHindi: '',
      system: '',
      bottle_1_primary_functional: '',
      bottle_1_supportive: '',
      bottle_2_primary_functional: '',
      bottle_2_supportive: '',
      bottle_3_chronic: '',
      bottle_4_degeneration: '',
      active: true,
    },
  });

  const activeStatus = watch('active');

  /* ================= GET BY ID ================= */
  const { data: editData } = useQuery({
    queryKey: ['getSavedDiseaseProtocolById', id],
    queryFn: () => getSavedDiseaseProtocolById(id),
    enabled: !!id,
  });

  /* ================= CREATE ================= */
  const createMutation = useMutation({
    mutationFn: createSystemAxis,
    onSuccess: () => {
      toast({
        title: 'System axis created successfully',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top-center',
      });
      reset();
      onSuccess?.();
    },
    onError: (error) => {
      toast({
        title: 'Failed to create system axis',
        description: error?.response?.data?.message || error?.message,
        status: 'error',
      });
    },
  });

  /* ================= UPDATE ================= */
  const updateMutation = useMutation({
    mutationFn: updateSystemAxis,
    onSuccess: () => {
      toast({
        title: 'System axis updated successfully',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top-center',
      });
      reset();
      onSuccess?.();
    },
    onError: (error) => {
      toast({
        title: 'Failed to update system axis',
        description: error?.response?.data?.message || error?.message,
        status: 'error',
      });
    },
  });

  /* ================= SUBMIT ================= */
  const submitHandler = (formData) => {
    if (id) {
      updateMutation.mutate({ id, ...formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const handleBottleChange = (key, index, value) => {
    const parts = dataEdit?.[key]
      ? dataEdit[key].split('+').map((v) => v.trim())
      : [];

    parts[index] = value;

    setDataEdit((prev) => ({
      ...prev,
      [key]: parts.join(' + '),
    }));
  };

  return (
    <Box w="100%" mt={6} display="flex" justifyContent="center">
      <Card w="100%" maxW="900px" rounded="xl" shadow="md">
        <CardBody
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <form
            style={{ marginTop: '20px' }}
            onSubmit={handleSubmit(submitHandler)}
          >
            {/* ROW 1 */}
            <Flex gap={4} mb={10}>
              <VStack
                spacing={4}
                align="stretch"
                position="relative"
                zIndex={1}
              >
                {/* BOTTLE 1 */}
                <Flex align="center" gap={4}>
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    // color={textMuted}
                    minW="80px"
                  >
                    BOTTLE 1
                  </Text>
                  <Box flex={1}>
                    <BottleItem
                      value={editData?.bottle1}
                      isEdit={true}
                      suggestions={
                        editData?.data?.result?.bottle12Remedies || []
                      }
                      onChange={(i, v) => handleBottleChange('bottle1', i, v)}
                    />
                  </Box>
                </Flex>

                {/* BOTTLE 2 */}
                <Flex align="center" gap={4}>
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    // color={textMuted}
                    minW="80px"
                  >
                    BOTTLE 2
                  </Text>
                  <Box flex={1}>
                    <BottleItem
                      value={editData?.bottle2}
                      isEdit={true}
                      suggestions={
                        editData?.data?.result?.bottle12Remedies || []
                      }
                      onChange={(i, v) => handleBottleChange('bottle2', i, v)}
                    />
                  </Box>
                </Flex>

                {/* BOTTLE 3 */}
                <Flex align="center" gap={4}>
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    // color={textMuted}
                    minW="80px"
                  >
                    BOTTLE 3
                  </Text>
                  <Box flex={1}>
                    <BottleItem
                      value={editData?.bottle3}
                      isEdit={true}
                      suggestions={
                        editData?.data?.result?.bottle34Remedies || []
                      }
                      onChange={(i, v) => handleBottleChange('bottle3', i, v)}
                    />
                  </Box>
                </Flex>

                {/* BOTTLE 4 */}
                <Flex align="center" gap={4}>
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    // color={textMuted}
                    minW="80px"
                  >
                    BOTTLE 4
                  </Text>
                  <Box flex={1}>
                    <BottleItem
                      isEdit={true}
                      value={editData?.bottle4}
                      suggestions={
                        editData?.data?.result?.bottle34Remedies || []
                      }
                      onChange={(i, v) => handleBottleChange('bottle4', i, v)}
                    />
                  </Box>
                </Flex>
              </VStack>
            </Flex>

            {/* SUBMIT */}
            <Flex justify="center">
              <Button
                type="submit"
                colorScheme="teal"
                isLoading={isLoading}
                w="160px"
              >
                {id ? 'Save' : 'Save'}
              </Button>
            </Flex>
          </form>
        </CardBody>
      </Card>
    </Box>
  );
}

export default SavedDiseaseProtocolForm;
